import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { clearCookieConsent } from '@/lib/cookie-consent';

interface LegalDocumentIntro {
  label: string;
  summary: string;
}

interface LegalDocumentParagraphBlock {
  type: 'paragraph';
  content: string;
}

interface LegalDocumentListBlock {
  type: 'list';
  items: string[];
}

type LegalDocumentBlock = LegalDocumentParagraphBlock | LegalDocumentListBlock;

interface LegalDocumentSection {
  title: string;
  blocks: LegalDocumentBlock[];
}

export type LegalDocumentKey = 'privacy' | 'terms' | 'cookies';

interface LegalDocumentPageProps {
  documentKey: LegalDocumentKey;
}

export const LegalDocumentPage = ({ documentKey }: LegalDocumentPageProps) => {
  const { t } = useTranslation(['legal', 'common']);
  const intro = t(`${documentKey}.intro`, { returnObjects: true }) as unknown as LegalDocumentIntro;
  const sections = t(`${documentKey}.sections`, { returnObjects: true }) as unknown as LegalDocumentSection[];
  const lastUpdatedDate = t(`${documentKey}.lastUpdatedDate`);

  const resetCookieChoice = () => {
    clearCookieConsent();
    window.location.assign('/');
  };

  return (
    <div className="min-h-dvh bg-background text-fg">
      <header className="border-b border-border/80 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-4 sm:h-16 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-0 lg:px-8">
          <Link to="/" className="shrink-0 text-brand transition-colors hover:text-brand-hover">
            <span className="block text-section-title font-semibold tracking-tight">{t('app.name', { ns: 'common' })}</span>
            <span className="mt-0.5 block text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-brand/75">
              {t('app.descriptor', { ns: 'common' })}
            </span>
          </Link>
          <nav className="hidden flex-wrap items-center justify-end gap-x-3 gap-y-1 text-body-sm sm:flex">
            <Link to="/privacy" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.privacy')}
            </Link>
            <Link to="/cookies" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.cookies')}
            </Link>
            <Link to="/terms" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.terms')}
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="mr-2 size-4" aria-hidden />
              {t('common:action.back')}
            </Link>
          </Button>
        </div>

        <div className="mb-10 rounded-xl border border-border bg-surface-sunken p-6 sm:p-8">
          <p className="text-overline font-semibold uppercase tracking-wider text-brand">{intro.label}</p>
          <h1 className="mt-3 text-balance text-[1.75rem] font-semibold tracking-tight text-fg sm:text-[2rem]">
            {t(`${documentKey}.title`)}
          </h1>
          <p className="mt-2 text-body-sm text-fg-secondary">
            {t(`${documentKey}.lastUpdated`, { date: lastUpdatedDate })}
          </p>
          <p className="mt-5 max-w-3xl text-body leading-relaxed text-fg-secondary">{intro.summary}</p>
        </div>

        <div className="space-y-6">
          {sections.map((section, sectionIndex) => (
            <section
              key={section.title}
              className="rounded-xl border border-border bg-surface p-6 shadow-e1 sm:p-8"
            >
              <h2 className="text-card-title font-semibold text-fg">{section.title}</h2>
              <div className="mt-4 space-y-4">
                {section.blocks.map((block, blockIndex) => {
                  if (block.type === 'list') {
                    return (
                      <ul
                        key={`${sectionIndex}-${blockIndex}`}
                        className="list-disc space-y-2 pl-5 text-body leading-relaxed text-fg-secondary marker:text-fg-tertiary"
                      >
                        {block.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    );
                  }

                  return (
                    <p
                      key={`${sectionIndex}-${blockIndex}`}
                      className="text-body leading-relaxed text-fg-secondary"
                    >
                      {block.content}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}

          {documentKey === 'cookies' ? (
            <section className="rounded-xl border border-border bg-surface p-6 shadow-e1 sm:p-8">
              <h2 className="text-card-title font-semibold text-fg">{t('cookies.preferences.title')}</h2>
              <p className="mt-4 text-body leading-relaxed text-fg-secondary">
                {t('cookies.preferences.body')}
              </p>
              <div className="mt-6 flex justify-end">
                <Button type="button" variant="outline" onClick={resetCookieChoice}>
                  {t('cookies.preferences.reset')}
                </Button>
              </div>
            </section>
          ) : null}
        </div>
      </main>

      <footer className="mt-12 border-t border-border bg-surface-sunken px-4 py-8 sm:px-6">
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-body-sm text-fg-secondary">{t('footer.company')}</p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-body-sm">
            <Link to="/privacy" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.privacy')}
            </Link>
            <Link to="/cookies" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.cookies')}
            </Link>
            <Link to="/terms" className="text-fg-secondary transition-colors hover:text-fg">
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
